import { atom } from 'jotai';
import axiosService from './axios.service';
import { store } from './atom.service';
import { getNotifications } from './notification.service';

/**
 * What's happening in each room right now. Small, so every open page can poll it.
 */
export interface SessionLiveState {
    _id: string;
    slug: string;
    name: string;
    // idea ids in presenting order
    ideas: string[];
    activeIdea: string | null;
    activeSince: Date | null;
    presented: string[];
    // teams the hosts left out of the lineup: in the room, but not presenting or judged
    leftOut: string[];
    // the room's own switch; ignored when the hackathon has only one room
    votingOpen: boolean;
    liveUpdatedAt: Date | null;
    descriptionUpdatedAt: Date | null;
}

export interface LiveBoard {
    // hackathon-wide voting switch
    votingPeriodOn: boolean;
    // winners announced
    resultsAnnounced?: boolean;
    // changes when the hackathon or its teams change, so open pages know to refetch them
    version?: string;
    sessions: SessionLiveState[];
}

export interface SessionLiveChanges {
    activeIdea?: string | null;
    presented?: string[];
    votingOpen?: boolean;
    // reorder only: must contain exactly the session's lineup
    ideas?: string[];
    // the whole left-out list (teams in the room that don't present or get judged)
    leftOut?: string[];
}

// live board per hackathon id (as in the URL)
export const liveBoardsAtom = atom<Record<string, LiveBoard>>({});

const toDate = (value: unknown) => value ? new Date(value as string) : null;

const convert = (data: any): SessionLiveState => ({
    ...data,
    leftOut: data.leftOut || [],
    activeSince: toDate(data.activeSince),
    liveUpdatedAt: toDate(data.liveUpdatedAt),
    descriptionUpdatedAt: toDate(data.descriptionUpdatedAt)
});

export const getLiveBoard = async (hackathonId: string) => {
    const { data } = await axiosService.get(`/api/hackathons/${hackathonId}/sessions/live`);
    const board: LiveBoard = {
        votingPeriodOn: !!data.votingPeriodOn,
        resultsAnnounced: !!data.resultsAnnounced,
        version: data.version,
        sessions: (data.sessions as any[]).map(convert)
    };
    // a new team on stage, or voting opening, means a new line in the bell; fetch it now rather than at the bell's
    // next minute (a moment later: the server writes it just after the change)
    const previous = store.get(liveBoardsAtom)[hackathonId];
    const onStage = (b: LiveBoard) => b.sessions.map(s => s.activeIdea || '').join(',');
    // (and when the board's version moves: an organizer's "Last call" bumps it)
    if (previous && ((onStage(previous) !== onStage(board) && board.sessions.some(s => s.activeIdea))
        || (!previous.votingPeriodOn && board.votingPeriodOn)
        || (board.votingPeriodOn && previous.version !== board.version))) {
        window.setTimeout(() => getNotifications().catch(() => { }), 1500);
    }
    store.set(liveBoardsAtom, current => ({ ...current, [hackathonId]: board }));
    return board;
}

export const updateLiveSession = async (hackathonId: string, sessionId: string, changes: SessionLiveChanges) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/sessions/${sessionId}/live`, changes);
    const updated = convert(data);
    store.set(liveBoardsAtom, current => {
        const board = current[hackathonId] || { votingPeriodOn: false, sessions: [] };
        return {
            ...current,
            [hackathonId]: { ...board, sessions: board.sessions.map(s => s._id === updated._id ? updated : s) }
        };
    });
    return updated;
}

/**
 * Advances a room: the current team is marked presented and the next unpresented team in order starts.
 * With no current team, starts the first unpresented one.
 */
export const nextPresenterChanges = (live: SessionLiveState): SessionLiveChanges => {
    const presented = new Set(live.presented);
    if (live.activeIdea) {
        presented.add(live.activeIdea);
    }
    const startFrom = live.activeIdea ? live.ideas.indexOf(live.activeIdea) + 1 : 0;
    const ordered = [...live.ideas.slice(startFrom), ...live.ideas.slice(0, startFrom)];
    const next = ordered.find(id => !presented.has(id)) || null;
    return {
        activeIdea: next,
        presented: live.ideas.filter(id => presented.has(id))
    };
}
